import { MapOverlayVersion, MapOverlayVersionStatus } from "@/map/types/overlay";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/components/ui/select";
import { Badge } from "@/ui/components/ui/badge";
import { Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

interface MapOverlayVersionSelectorProps {
  versions: MapOverlayVersion[];
  selectedVersion: string | undefined;
  onVersionChange: (version: string) => void;
  isLoading?: boolean;
}

const getStatusBadgeVariant = (status: MapOverlayVersionStatus): "default" | "secondary" | "outline" => {
  switch (status) {
    case "active":
      return "default";
    case "visible":
      return "secondary";
    case "obsolete":
      return "outline";
    default:
      return "outline";
  }
};

const getStatusLabel = (
  t: TFunction<"map">,
  status: MapOverlayVersionStatus,
): string => {
  switch (status) {
    case "active":
      return t("overlay.status.active");
    case "visible":
      return t("overlay.status.visible");
    case "obsolete":
      return t("overlay.status.obsolete");
    default:
      return status;
  }
};

export const MapOverlayVersionSelector = ({
  versions,
  selectedVersion,
  onVersionChange,
  isLoading = false,
}: MapOverlayVersionSelectorProps) => {
  const { t } = useTranslation("map");

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 bg-background border border-border rounded-md px-3 py-2 shadow-md">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-xs text-muted-foreground">{t("overlay.loadingVersions")}</span>
      </div>
    );
  }

  if (versions.length === 0) {
    return null;
  }

  // Find the active version for display
  const activeVersion = versions.find((v) => v.status === "active");
  const displayVersion = selectedVersion || activeVersion?.version;

  return (
    <div className="bg-background border border-border rounded-md shadow-md">
      <Select value={displayVersion} onValueChange={onVersionChange}>
        <SelectTrigger className="h-9 min-w-[180px] border-0 shadow-none">
          <SelectValue placeholder={t("overlay.selectVersion")} />
        </SelectTrigger>
        <SelectContent>
          {versions.map((version) => (
            <SelectItem key={version.version} value={version.version}>
              <div className="flex items-center gap-2">
                <span className="text-sm">{version.version}</span>
                <Badge
                  variant={getStatusBadgeVariant(version.status)}
                  className="text-[10px] px-1.5 py-0"
                >
                  {getStatusLabel(t, version.status)}
                </Badge>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};
